import { useState } from "react";
import { CircleArrowRight, Play } from "lucide-react";
import { useNavigate } from "react-router-dom";

import WidgetCard from "./widget-card";
import VideoMedia from "../videos/video-media";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { QueryKeys } from "@/constants";
import ScreenRouteNames from "@/constants/screen-route-names";
import { queryClient } from "@/hooks/use-query-client";
import useUserInfo from "@/hooks/use-user-info";
import { useVideoList } from "@/hooks/use-videos";
import type { GlobalFlag } from "@/model/global-flag";
import type Widget from "@/model/widget";

/** Dashboard card that plays the first video in place; "View All" opens the Videos screen. */
const VideoWidget = ({ widget }: { widget: Widget }) => {
  const userInfo = useUserInfo();
  const navigate = useNavigate();
  const { data: videoList = [], isLoading } = useVideoList();
  const [isPlaying, setIsPlaying] = useState(false);

  const showVideos = queryClient.getQueryData<{ data?: GlobalFlag[] }>([
    QueryKeys.GLOBAL_FLAGS,
    userInfo.username,
  ])?.data?.[0]?.showVideos;

  if (!showVideos) return null;

  const firstVideo = videoList[0];

  const footerContent = (
    <Button
      variant="link"
      className="w-full"
      onClick={() => navigate(`/${ScreenRouteNames.VIDEOS.path}`)}
    >
      View All <CircleArrowRight className="ml-1" />
    </Button>
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="space-y-3">
          <Skeleton className="aspect-video w-full bg-gray-200" />
          <Skeleton className="h-4 w-3/4 bg-gray-200" />
        </div>
      );
    }

    if (!firstVideo) {
      return (
        <p className="py-8 text-center text-sm font-medium text-gray-500">
          No videos available
        </p>
      );
    }

    return (
      <div className="space-y-3">
        <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-black">
          {isPlaying ? (
            <VideoMedia video={firstVideo} />
          ) : (
            // Thumbnail first so the dashboard doesn't autoplay
            <button
              type="button"
              onClick={() => setIsPlaying(true)}
              aria-label={`Play ${firstVideo.topic}`}
              className="group absolute inset-0"
            >
              <img
                src={firstVideo.thumbNail}
                alt=""
                className="size-full object-cover"
              />
              <span className="absolute inset-0 flex items-center justify-center bg-black/20 transition-colors group-hover:bg-black/40">
                <span className="flex size-12 items-center justify-center rounded-full bg-white/90 shadow-md">
                  <Play className="ml-0.5 size-5 fill-current text-foreground" />
                </span>
              </span>
            </button>
          )}
        </div>

        <div>
          <p className="font-medium leading-snug">{firstVideo.topic}</p>
          <p className="text-sm text-muted-foreground">
            {firstVideo.source} · {firstVideo.type}
          </p>
        </div>
      </div>
    );
  };

  return (
    <WidgetCard title={widget?.widgetDisplayName} footerContent={footerContent}>
      {renderContent()}
    </WidgetCard>
  );
};

export default VideoWidget;
